import React, { useEffect, useState } from 'react'
import { HandThumbDownIcon, HandThumbUpIcon } from '@heroicons/react/24/outline'
import { HandThumbDownIcon as HandThumbDownSolidIcon, HandThumbUpIcon as HandThumbUpSolidIcon } from '@heroicons/react/24/solid'
import { ApiError } from '../../config/api'
import {
  getJob,
  getQuota,
  getSession,
  sendChat,
  sendFeedback,
  SUGGESTIONS,
  type AskEvansMessage,
  type AskEvansQuota
} from '../../api/askEvansApi'
import { Citations } from './Citations'
import { MarkdownBody } from './MarkdownBody'
import { SourceChips } from './SourceChips'

type ChatViewProps = {
  variant?: 'page' | 'panel'
  sessionId?: string | null
  onSessionChange?: (sessionId: string) => void
}

async function waitForJob(jobId: string) {
  const started = Date.now()
  for (;;) {
    const job = await getJob(jobId)
    if (job.status === 'completed' || job.status === 'failed') return job
    if (Date.now() - started > 210_000) {
      throw new Error('Ask Evans took too long. Try again.')
    }
    await new Promise((resolve) => setTimeout(resolve, 1200))
  }
}

export function ChatView({ variant = 'page', sessionId, onSessionChange }: ChatViewProps) {
  const [messages, setMessages] = useState<AskEvansMessage[]>([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [quota, setQuota] = useState<AskEvansQuota | null>(null)
  const [activeSession, setActiveSession] = useState<string | null>(sessionId || null)

  const isPanel = variant === 'panel'

  useEffect(() => {
    let cancelled = false
    Promise.resolve(getQuota())
      .then((next) => {
        if (!cancelled && next) setQuota(next)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    setActiveSession(sessionId || null)
    if (!sessionId) {
      setMessages([])
      return
    }
    let cancelled = false
    getSession(sessionId)
      .then((detail) => {
        if (!cancelled) setMessages(detail.messages)
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load this chat')
      })
    return () => {
      cancelled = true
    }
  }, [sessionId])

  async function onSend(text?: string) {
    const message = (text ?? draft).trim()
    if (!message || sending) return
    setDraft('')
    setError(null)
    setSending(true)
    setMessages((prev) => [...prev, { id: `local-${Date.now()}`, role: 'user', content: message }])
    try {
      const accepted = await sendChat(message, activeSession)
      if (accepted.session_id !== activeSession) {
        setActiveSession(accepted.session_id)
        onSessionChange?.(accepted.session_id)
      }
      const job = await waitForJob(accepted.job_id)
      setQuota(job.quota)
      if (job.status === 'failed') {
        setError(job.error || 'Ask Evans could not answer')
        return
      }
      setMessages((prev) => [
        ...prev,
        {
          id: job.assistant_message_id || job.job_id,
          role: 'assistant',
          content: job.answer || '',
          query_log_id: job.assistant_message_id || undefined,
          sources: job.sources || undefined,
          citations: job.citations || undefined
        }
      ])
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setError('A question is already running in this chat.')
      } else if (err instanceof ApiError && (err.status === 429 || /quota/i.test(err.message))) {
        setError(err.message)
      } else {
        setError(err instanceof Error ? err.message : 'Ask Evans could not answer')
      }
    } finally {
      setSending(false)
    }
  }

  async function rate(message: AskEvansMessage, rating: 1 | -1) {
    const messageId = message.query_log_id
    if (!activeSession || !messageId) return
    try {
      await sendFeedback(activeSession, messageId, rating)
      setMessages((prev) => prev.map((item) => (item.id === message.id ? { ...item, user_rating: rating } : item)))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save feedback')
    }
  }

  function confidenceOf(message: AskEvansMessage): 'high' | 'medium' | 'low' | null {
    const stored = (message as AskEvansMessage & { confidence?: string }).confidence
    if (stored === 'high' || stored === 'medium' || stored === 'low') return stored
    const count = message.citations?.length || 0
    if (!count) return null
    return count >= 3 ? 'high' : 'medium'
  }

  return (
    <div className={isPanel ? 'flex h-full min-h-0 flex-col bg-white' : 'flex h-full min-h-0 flex-col bg-page-bg'}>
      <div className={`min-h-0 flex-1 overflow-y-auto ${isPanel ? 'space-y-4 px-4 py-4' : 'space-y-6 px-8 py-6'}`}>
        {messages.length === 0 ? (
          isPanel ? (
            <div className="flex flex-col gap-2.5 pt-1">
              <p className="font-sans text-[12px] text-[#9CA3AF]">Try asking:</p>
              <div className="flex flex-col gap-2">
                {SUGGESTIONS.map((prompt) => (
                  <button
                    key={prompt}
                    type="button"
                    disabled={sending}
                    onClick={() => void onSend(prompt)}
                    className="rounded-lg border border-[#E5E7EB] bg-white px-3.5 py-2.5 text-left font-sans text-[13px] leading-snug text-[#4B5563] shadow-[0_1px_2px_rgba(15,23,42,0.04)] transition hover:border-[#D1D5DB] hover:bg-[#FAFAFA] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mx-auto mt-16 flex max-w-lg flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-hero-red/20 bg-hero-red/10 shadow-sm">
                <svg className="h-6 w-6 text-hero-red" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
              </div>
              <h3 className="font-heading text-[18px] font-semibold text-blueprint-blue">Ask about WPS Studio</h3>
              <p className="mt-2 font-sans text-[13px] leading-relaxed text-text-grey">
                Ask about projects, procedures, floor sheets, team members, or mechanical tests. Ask Evans chooses the
                right record for each question.
              </p>
            </div>
          )
        ) : (
          <div className={`mx-auto space-y-6 ${isPanel ? 'max-w-none space-y-4' : 'max-w-3xl'}`}>
            {messages.map((message, index) => (
              <div
                key={message.id || `message-${index}`}
                className={`flex items-start ${message.role === 'user' ? 'justify-end' : 'justify-start'} ${isPanel ? 'gap-2.5' : 'gap-4'}`}
              >
                {message.role === 'assistant' && (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hero-red font-heading text-[12px] font-bold text-white shadow-sm">
                    AE
                  </div>
                )}
                <div
                  className={`rounded-xl border shadow-sm ${isPanel ? 'p-3' : 'p-4'} ${
                    message.role === 'user'
                      ? 'max-w-[85%] rounded-tr-none border-blueprint-blue bg-blueprint-blue text-white'
                      : 'min-w-0 flex-1 rounded-tl-none border-[#E2E5EA] bg-white text-text-grey'
                  }`}
                >
                  <div
                    className={`font-sans text-[13px] leading-relaxed ${
                      message.role === 'user' ? 'whitespace-pre-wrap' : 'ask-evans-prose'
                    }`}
                  >
                    {message.role === 'assistant' ? <MarkdownBody content={message.content} /> : message.content}
                  </div>
                  {message.role === 'assistant' && (
                    <>
                      <SourceChips sources={message.sources} />
                      <Citations citations={message.citations} />
                      {confidenceOf(message) && (
                        <div className="mt-3 flex items-center gap-2 border-t border-[#EEF0F3] pt-2.5">
                          <span className="text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">Confidence:</span>
                          <span
                            className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.02em] ${
                              confidenceOf(message) === 'high'
                                ? 'border-galvanizing-green/20 bg-galvanizing-green/10 text-galvanizing-green'
                                : confidenceOf(message) === 'medium'
                                  ? 'border-[#FFCA57]/30 bg-[#FFCA57]/10 text-[#8A6500]'
                                  : 'border-readable-red/20 bg-readable-red/10 text-readable-red'
                            }`}
                          >
                            {confidenceOf(message)}
                          </span>
                        </div>
                      )}
                      <div className="mt-3 flex items-center gap-1 border-t border-[#EEF0F3] pt-2.5">
                        <span className="mr-1 text-[10px] font-semibold uppercase tracking-wider text-heritage-grey">Feedback</span>
                        <button type="button" aria-label="Helpful" aria-pressed={message.user_rating === 1} onClick={() => void rate(message, 1)}>
                          {message.user_rating === 1 ? (
                            <HandThumbUpSolidIcon className="h-4 w-4 text-galvanizing-green" />
                          ) : (
                            <HandThumbUpIcon className="h-4 w-4 text-heritage-grey" />
                          )}
                        </button>
                        <button type="button" aria-label="Not helpful" aria-pressed={message.user_rating === -1} onClick={() => void rate(message, -1)}>
                          {message.user_rating === -1 ? (
                            <HandThumbDownSolidIcon className="h-4 w-4 text-readable-red" />
                          ) : (
                            <HandThumbDownIcon className="h-4 w-4 text-heritage-grey" />
                          )}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-hero-red font-heading text-[12px] font-bold text-white">
                  AE
                </div>
                <div className="rounded-xl rounded-tl-none border border-[#E2E5EA] bg-white px-4 py-3 text-[12px] text-heritage-grey">
                  Searching WPS Studio…
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      {error && (
        <p className="px-4 text-sm text-readable-red" role="alert">
          {error}
        </p>
      )}
      <div className={`shrink-0 ${isPanel ? 'border-t border-[#E5E7EB] bg-white px-4 pb-3 pt-3' : 'border-t border-[#E2E5EA] bg-white px-8 py-4'}`}>
        <form
          className={isPanel ? '' : 'mx-auto max-w-3xl'}
          onSubmit={(event) => {
            event.preventDefault()
            void onSend()
          }}
        >
          <div className={`flex items-center ${isPanel ? 'gap-2' : 'gap-3'}`}>
            <input
              aria-label="Ask Evans"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              placeholder={isPanel ? 'Ask about WPS Studio data...' : 'Ask a question about WPS Studio...'}
              disabled={sending}
              className={`flex-1 border font-sans text-[13px] focus:outline-none ${
                isPanel
                  ? 'rounded-xl border-[#E5E7EB] bg-white px-3.5 py-2.5 text-[#1F2937] placeholder:text-[#9CA3AF]'
                  : 'rounded-lg border-[#E2E5EA] bg-white px-4 py-2.5 focus:ring-2 focus:ring-hero-red/50'
              }`}
            />
            {isPanel ? (
              <button
                type="submit"
                aria-label="Send"
                disabled={sending || !draft.trim()}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#9CA3AF] text-white transition hover:bg-[#6B7280] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
                </svg>
              </button>
            ) : (
              <button
                type="submit"
                disabled={sending || !draft.trim()}
                className="flex items-center gap-1.5 rounded-lg bg-hero-red px-5 py-2.5 text-[13px] font-medium text-white shadow-sm transition hover:bg-hero-red-60 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <span>{sending ? 'Searching...' : 'Send'}</span>
                {!sending && (
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                )}
              </button>
            )}
          </div>
          {isPanel ? (
            <p className="mt-2.5 text-center font-sans text-[10px] leading-snug text-[#9CA3AF]">
              Ask Evans can make mistakes. Check important figures.
            </p>
          ) : (
            quota && (
              <div className="mt-2.5 flex items-center justify-between gap-3 font-sans text-[11px] text-text-grey" data-testid="ask-evans-quota">
                <div className="flex min-w-0 flex-col gap-1">
                  <span>
                    Token Usage: <strong>{quota.percent}%</strong> used this month
                  </span>
                  <span className="text-[10px] text-heritage-grey">{quota.resetHint}</span>
                </div>
                <div className="h-1.5 w-32 shrink-0 overflow-hidden rounded-full border border-[#E2E5EA] bg-page-bg">
                  <div className="h-full rounded-full bg-hero-red" style={{ width: `${Math.min(100, quota.percent)}%` }} />
                </div>
              </div>
            )
          )}
        </form>
      </div>
    </div>
  )
}
